
import { computed, defineComponent, reactive, ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { AgreementsRouteNames } from '@/router/routes-names'
import { InvoicesListModel, useInvoicesModule, InvoiceStatus } from '@/modules/invoices'
import { useAgreementModule } from '@/modules/agreements'
import InvoicesShowInvoice from '@/components/invoices/dialogs/show-invoice.vue'
import { useAPIPagination } from '@/views/_shared/search-list/pagination'
import { InvoiceSearchFilter } from '@/views/_shared/search-list/types'
import { useSessionModule } from '@/modules/session'
import moment from 'moment'
import InvoicesFilter from '@/components/invoices/invoices-filter.vue'
import useQuasar from 'quasar/src/composables/use-quasar.js';
import { FormModelCacheTag, restoreFormModelTo } from '@/modules/_shared/forms'
import Uuid from '@/components/_shared/uuid.vue'
import config from '@/modules/_shared/config'
import InvoicesShowExternalLog from '@/components/invoices/dialogs/show-invoicing-external-log.vue'

export default defineComponent({
    name: 'view-invoices-list',
    components: { Uuid, InvoicesShowInvoice, InvoicesFilter },
    props: {
        restoreFilters: {
            type: [Boolean, String]
        }
    },
    async setup (props) {
        const router = useRouter()
        const route = useRoute()
        const {
            getInvoices,
            invoices,
            invoicesFilter,
            invoicesCount,
            getFormattedPrice,
            getInvoice,
            currentInvoice,
            setInvoicesAgreementFilter,
            createAllInvoices
        } = useInvoicesModule()
        const { getAgreementsSimplified, agreementsSimplifiedById } = useAgreementModule()
        const { profile, isOperatorInvoicingManager } = useSessionModule()
        const $q = useQuasar()

        const showInvoice = ref(false)

        const invoicesColumns = ([
            { name: 'invoiceId', label: 'Invoice id', field: 'id', sortable: true, align: 'left' },
            { name: 'resourceName', label: 'Agreement', field: 'resourceName', sortable: true, align: 'left' },
            { name: 'customerName', label: 'Customer name', field: 'customerName', sortable: true, align: 'left' },
            { name: 'from', label: 'From', field: 'periodStart', sortable: true, align: 'left' },
            { name: 'to', label: 'To', field: 'periodEnd', sortable: true, align: 'left' },
            { name: 'totalGrossAmount', label: 'Total value', field: (row: InvoicesListModel) => getFormattedPrice(row.totalGrossAmount, config.defaultCurrency), sortable: true, align: 'left' },
            { name: 'created', label: 'Created', field: 'createdAt', sortable: true, align: 'left' },
            { name: 'status', label: 'Status', field: 'status', sortable: true, align: 'left' },
            { name: 'actions', label: 'Actions', field: 'actions', align: 'left' }
        ])

        const invoiceCreationInProgress = ref(false)

        async function onCreateInvoices () {
            const operatorId: string = profile.value?.id ? profile.value.id : ''
            if (!operatorId) {
                return
            }
            invoiceCreationInProgress.value = true
            await createAllInvoices(moment().format('YYYY-MM-DD'), operatorId)
            $q.notify({
                color: 'positive',
                message: 'Invoice creation in progress. You may refresh the list in couple of seconds.'
            })
            setTimeout(() => {
                invoiceCreationInProgress.value = false
            }, 2000)
        }

        async function onShowInvoiceRow (evt: PointerEvent | null, props: InvoicesListModel) {
            await getInvoice(props.id as string)
            showInvoice.value = true
        }

        function onShowExternalLogs (props: InvoicesListModel) {
            $q.dialog({
                component: InvoicesShowExternalLog,
                componentProps: {
                    invoiceId: props.id
                }
            })
        }

        function onHideInvoiceView () {
            showInvoice.value = false
        }

        function onShowAgreement (props: InvoicesListModel) {
            router.push({ name: AgreementsRouteNames.SHOW, params: { agreementId: props.resourceId }, query: { page: route.query.page } })
        }

        const getInvoiceStatusColors = (status: string) => {
            switch (status) {
            case InvoiceStatus.Created:
            case InvoiceStatus.Sending:
            case InvoiceStatus.Sent:
                return 'positive'
            case InvoiceStatus.Failed:
                return 'waring'
            case InvoiceStatus.Revoked:
                return 'negative'
            }
        }

        const currentPage = computed(() => route.query.page === null ? '1' : route.query.page)
        if (props.restoreFilters !== false) {
            restoreFormModelTo(invoicesFilter.search, FormModelCacheTag.INVOICES_LIST_FILTERS)
        }

        const { loading, pagination, onRequest } = useAPIPagination(invoicesFilter, getInvoices, invoicesCount, currentPage, false, FormModelCacheTag.INVOICES_LIST_FILTERS)

        onRequest({
            filter: invoicesFilter.search || {
                status: '',
                agreementName: '',
                search: '',
                organization: '',
                dateFrom: '',
                dateTo: ''
            } as InvoiceSearchFilter,
            pagination: pagination.value
        })

        let filter = invoicesFilter.search as InvoiceSearchFilter
        filter = reactive(filter)

        function onResetFilters () {
            filter.status = 'all'
            filter.agreementName = ''
            filter.search = ''
            filter.organization = ''
            filter.dateFrom = ''
            filter.dateTo = ''
        }

        onMounted(() => {
            filter.search = window.localStorage.getItem('invoicesAgreementFilter') || ''
        })

        onUnmounted(() => {
            setInvoicesAgreementFilter('')
        })

        if (agreementsSimplifiedById.value.size === 0) {
            await getAgreementsSimplified()
        }

        return {
            loading,
            onRequest,
            pagination,
            filter,
            invoices,
            invoicesColumns,
            onShowInvoiceRow,
            onShowAgreement,
            onShowExternalLogs,
            onResetFilters,
            showInvoice,
            currentInvoice,
            getInvoiceStatusColors,
            onHideInvoiceView,
            onCreateInvoices,
            invoiceCreationInProgress,
            isInvoicingManager: isOperatorInvoicingManager
        }
    }
})
